/* ==========================================================================
   Prescription History Page
   Provider view of past prescriptions with prescribe-again functionality
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { PageContainer } from '@/components/layout/PageContainer'
import { ProviderTabBar } from '@/components/layout/ProviderTabBar'
import { ProviderPageHeader } from '@/components/layout/ProviderPageHeader'
import { PrescribeAgainModal } from '@/components/provider/PrescribeAgainModal'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { usePortal, usePortalSlug } from '@/context/PortalContext'
import { getProviderPrescriptions, getPrescriptionPdfUrl } from '@/api/portal'
import { PrescriptionStatus } from '@/api/types'
import type { PrescriptionListItem, PrescriptionListResponse } from '@/api/types'
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  ClipboardList,
  FileDown,
  Search,
  Loader2,
} from 'lucide-react'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const PAGE_SIZE = 20

const STATUS_STYLES: Record<
  PrescriptionStatus,
  { label: string; className: string }
> = {
  [PrescriptionStatus.SUBMITTED]: {
    label: 'Submitted',
    className: 'bg-blue-100 text-blue-700',
  },
  [PrescriptionStatus.FAX_SENT]: {
    label: 'Fax Sent',
    className: 'bg-yellow-100 text-yellow-700',
  },
  [PrescriptionStatus.DELIVERED]: {
    label: 'Delivered',
    className: 'bg-green-100 text-green-700',
  },
  [PrescriptionStatus.FAILED]: {
    label: 'Failed',
    className: 'bg-red-100 text-red-700',
  },
  // Only reachable for pharmacies with shipment tracking enabled. This map is a
  // total Record over the status union, so the entry is required for the build —
  // it is not an opt-in to showing `shipped` in the status filter.
  [PrescriptionStatus.SHIPPED]: {
    label: 'Shipped',
    className: 'bg-emerald-100 text-emerald-700',
  },
}

const SORT_OPTIONS = [
  { value: 'date-desc', label: 'Date (Newest)' },
  { value: 'date-asc', label: 'Date (Oldest)' },
  { value: 'drug_name-asc', label: 'Drug Name (A-Z)' },
  { value: 'drug_name-desc', label: 'Drug Name (Z-A)' },
] as const

/* --------------------------------------------------------------------------
   Status Badge
   -------------------------------------------------------------------------- */

function StatusBadge({ status }: { status: PrescriptionStatus }) {
  const style = STATUS_STYLES[status] || {
    label: status,
    className: 'bg-gray-100 text-gray-700',
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  )
}

/* --------------------------------------------------------------------------
   Loading Skeleton
   -------------------------------------------------------------------------- */

function TableSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-12 bg-[var(--color-gray-100)] rounded" />
      ))}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getPatientDisplay(rx: PrescriptionListItem): { name: string; detail: string | null } {
  if (rx.patient_type === 'human') {
    const name = `${rx.patient_first_name || ''} ${rx.patient_last_name || ''}`.trim() || 'N/A'
    return { name, detail: null }
  }
  const animalName = rx.animal_name || 'N/A'
  const species = rx.animal_species ? ` (${rx.animal_species})` : ''
  const owner = rx.owner_name ? `Owner: ${rx.owner_name}` : null
  return { name: `${animalName}${species}`, detail: owner }
}

function canDownloadPdf(rx: PrescriptionListItem): boolean {
  // `shipped` sits past `delivered` in the lifecycle, so it must be included here
  // or a shipped prescription silently loses its download button (PORTAL-146).
  return (
    rx.has_pdf &&
    (rx.status === PrescriptionStatus.FAX_SENT ||
      rx.status === PrescriptionStatus.DELIVERED ||
      rx.status === PrescriptionStatus.SHIPPED)
  )
}

/* --------------------------------------------------------------------------
   View Fax Button
   -------------------------------------------------------------------------- */

function ViewFaxButton({ rx, slug }: { rx: PrescriptionListItem; slug: string }) {
  const [loading, setLoading] = useState(false)

  async function handleDownload() {
    setLoading(true)
    try {
      const url = await getPrescriptionPdfUrl(slug, rx.id)
      window.open(url, '_blank')
    } catch {
      // Silently fail — user can retry
    } finally {
      setLoading(false)
    }
  }

  if (!canDownloadPdf(rx)) return null

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleDownload}
      disabled={loading}
      title="View Fax PDF"
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <FileDown className="h-4 w-4" />
      )}
      <span className="ml-1">View Fax</span>
    </Button>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function PrescriptionHistory() {
  const slug = usePortalSlug()
  const { config } = usePortal()
  // PORTAL-192: tracking numbers the pharmacy typed in on its Faxes page.
  // Reads `manual_tracking_number` only, never the bot's `shipment`, so
  // bot-tracked pharmacies (PORTAL-146) keep their provider UI unchanged.
  const showTracking = !!config?.manual_tracking_enabled

  const [data, setData] = useState<PrescriptionListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [statusFilter, setStatusFilter] = useState<PrescriptionStatus | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sortValue, setSortValue] = useState('date-desc')

  // Prescribe again modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedRx, setSelectedRx] = useState<PrescriptionListItem | null>(
    null
  )

  const [sortBy, sortOrder] = sortValue.split('-') as [string, string]

  const fetchPrescriptions = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await getProviderPrescriptions(slug, {
        status: statusFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      setData(result)
    } catch {
      setError('Failed to load prescriptions. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [slug, page, statusFilter, dateFrom, dateTo, search, sortBy, sortOrder])

  useEffect(() => {
    fetchPrescriptions()
  }, [fetchPrescriptions])

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  function handlePrescribeAgain(rx: PrescriptionListItem) {
    setSelectedRx(rx)
    setModalOpen(true)
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value as PrescriptionStatus | '')
    setPage(0)
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSearch(searchInput)
    setPage(0)
  }

  function handleDateFromChange(value: string) {
    setDateFrom(value)
    setPage(0)
  }

  function handleDateToChange(value: string) {
    setDateTo(value)
    setPage(0)
  }

  function handleSortChange(value: string) {
    setSortValue(value)
    setPage(0)
  }

  return (
    <PageContainer size="lg" className="py-8">
      <ProviderPageHeader />

      <ProviderTabBar />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-gray-400)]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Ref ID, drug, or patient name..."
              className="pl-8 pr-3 py-2 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)] w-56"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-2 text-sm bg-[var(--pharmacy-primary)] text-white rounded-md hover:opacity-90 transition-opacity"
          >
            Search
          </button>
        </form>

        {/* Date From */}
        <div className="flex flex-col">
          <label className="text-xs text-[var(--color-gray-500)] mb-1">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => handleDateFromChange(e.target.value)}
            className="px-3 py-2 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)]"
          />
        </div>

        {/* Date To */}
        <div className="flex flex-col">
          <label className="text-xs text-[var(--color-gray-500)] mb-1">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => handleDateToChange(e.target.value)}
            className="px-3 py-2 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)]"
          />
        </div>

        {/* Status */}
        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="px-3 py-2 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)]"
        >
          <option value="">All Statuses</option>
          <option value={PrescriptionStatus.SUBMITTED}>Submitted</option>
          <option value={PrescriptionStatus.FAX_SENT}>Fax Sent</option>
          <option value={PrescriptionStatus.DELIVERED}>Delivered</option>
          <option value={PrescriptionStatus.FAILED}>Failed</option>
        </select>

        {/* Sort */}
        <select
          value={sortValue}
          onChange={(e) => handleSortChange(e.target.value)}
          className="px-3 py-2 text-sm border border-[var(--color-gray-300)] rounded-md bg-white text-[var(--color-charcoal)] focus:outline-none focus:ring-2 focus:ring-[var(--pharmacy-primary)]"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Refresh */}
        <button
          type="button"
          onClick={fetchPrescriptions}
          className="p-2 text-[var(--color-gray-500)] hover:text-[var(--color-charcoal)] transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <Card>
          <TableSkeleton />
        </Card>
      ) : error ? (
        <Card>
          <div className="py-8 text-center">
            <p className="text-[var(--color-error)] mb-4">{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchPrescriptions}>
              Try Again
            </Button>
          </div>
        </Card>
      ) : !data || data.prescriptions.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <ClipboardList className="h-12 w-12 text-[var(--color-gray-400)] mx-auto mb-3" />
            <p className="text-[var(--color-gray-600)] font-medium">
              No prescriptions found
            </p>
            <p className="text-sm text-[var(--color-gray-500)] mt-1">
              {statusFilter || search || dateFrom || dateTo
                ? 'Try adjusting your filters.'
                : 'Your submitted prescriptions will appear here.'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-gray-200)]">
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Date
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Ref ID
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Patient
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Drug Name
                      </th>
                      <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Status
                      </th>
                      {showTracking && (
                        <th className="text-left text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                          Tracking #
                        </th>
                      )}
                      <th className="text-right text-xs font-medium text-[var(--color-gray-500)] uppercase tracking-wider py-3 px-4">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-gray-100)]">
                    {data.prescriptions.map((rx) => {
                      const patient = getPatientDisplay(rx)
                      return (
                        <tr
                          key={rx.id}
                          className="hover:bg-[var(--color-gray-50)] transition-colors"
                        >
                          <td className="py-3 px-4 text-sm text-[var(--color-charcoal)]">
                            {formatDate(rx.submitted_at)}
                          </td>
                          <td className="py-3 px-4 text-sm text-[var(--color-gray-500)] font-mono">
                            {rx.reference_id}
                          </td>
                          <td className="py-3 px-4 text-sm text-[var(--color-charcoal)]">
                            <div>{patient.name}</div>
                            {patient.detail && (
                              <div className="text-xs text-[var(--color-gray-500)]">
                                {patient.detail}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-[var(--color-charcoal)]">
                            {rx.drug_name}
                          </td>
                          <td className="py-3 px-4">
                            <StatusBadge status={rx.status} />
                          </td>
                          {showTracking && (
                            <td className="py-3 px-4 text-sm text-[var(--color-charcoal)] font-mono whitespace-nowrap">
                              {rx.manual_tracking_number || (
                                <span className="text-[var(--color-gray-400)]">
                                  —
                                </span>
                              )}
                            </td>
                          )}
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <ViewFaxButton rx={rx} slug={slug} />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrescribeAgain(rx)}
                              >
                                Prescribe Again
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {data.prescriptions.map((rx) => {
              const patient = getPatientDisplay(rx)
              return (
                <Card key={rx.id}>
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-[var(--color-charcoal)]">
                          {rx.drug_name}
                        </p>
                        <p className="text-xs text-[var(--color-gray-500)] mt-0.5">
                          {formatDate(rx.submitted_at)}
                        </p>
                      </div>
                      <StatusBadge status={rx.status} />
                    </div>
                    <p className="text-xs text-[var(--color-gray-500)] font-mono">
                      {rx.reference_id}
                    </p>
                    <div className="text-sm text-[var(--color-gray-600)]">
                      <span>Patient: {patient.name}</span>
                      {patient.detail && (
                        <span className="block text-xs text-[var(--color-gray-500)]">
                          {patient.detail}
                        </span>
                      )}
                    </div>
                    {showTracking && rx.manual_tracking_number && (
                      <div className="text-sm text-[var(--color-gray-600)]">
                        <span>Tracking: </span>
                        <span className="font-mono">
                          {rx.manual_tracking_number}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <ViewFaxButton rx={rx} slug={slug} />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrescribeAgain(rx)}
                        className="flex-1 justify-center"
                      >
                        Prescribe Again
                      </Button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-[var(--color-gray-500)]">
                Showing {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, data.total)} of {data.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 rounded-md text-[var(--color-gray-500)] hover:bg-[var(--color-gray-100)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-[var(--color-gray-600)]">
                  {page + 1} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page >= totalPages - 1}
                  className="p-2 rounded-md text-[var(--color-gray-500)] hover:bg-[var(--color-gray-100)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Prescribe Again Modal */}
      <PrescribeAgainModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setSelectedRx(null)
        }}
        prescription={selectedRx}
      />
    </PageContainer>
  )
}

export default PrescriptionHistory
